<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { emitter } from 'shared/helpers/mitt';
import { PIPELINE_EVENTS } from 'dashboard/helper/pipelineCardCable';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import StartSequenceModal from './StartSequenceModal.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const props = defineProps({
  cardId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['sequenceStarted']);

const { t, locale } = useI18n();
const store = useStore();
const { showAlert } = useAlert();
const { isCloudFeatureEnabled } = useAccount();
const sequences = ref([]);
const isLoading = ref(false);
const showStartModal = ref(false);
const busySequenceIds = ref(new Set());
const isConfirmingCancel = ref(false);
const cancelConfirmationRef = ref(null);
const loadError = ref(false);
let fetchRequestId = 0;

const isSequenceBusy = sequenceId =>
  busySequenceIds.value.has(String(sequenceId));
const acquireSequenceLock = sequenceId => {
  const key = String(sequenceId);
  if (busySequenceIds.value.has(key)) return false;
  busySequenceIds.value = new Set(busySequenceIds.value).add(key);
  return true;
};
const releaseSequenceLock = sequenceId => {
  const next = new Set(busySequenceIds.value);
  next.delete(String(sequenceId));
  busySequenceIds.value = next;
};

// Audit 2026-05-07 — feature flag pipeline_sequences pode estar desligada
// para esta account (SuperAdmin). Quando desligada: nada renderiza, nada
// é fetchado, badges não aparecem.
const isSequencesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_SEQUENCES)
);

const activeSequences = computed(() =>
  sequences.value.filter(s => s.status === 'active' || s.status === 'paused')
);

const completedSequences = computed(() =>
  sequences.value.filter(
    s => s.status === 'completed' || s.status === 'cancelled'
  )
);

const fetchSequences = async () => {
  fetchRequestId += 1;
  const requestId = fetchRequestId;
  const cardId = props.cardId;
  sequences.value = [];
  loadError.value = false;
  if (!isSequencesEnabled.value) {
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await PipelineCardsAPI.getCardSequences(cardId);
    if (requestId !== fetchRequestId || cardId !== props.cardId) return;
    sequences.value = data.data || [];
    // Sync the kanban badge cache so PipelineCard badge updates reactively.
    store.dispatch('pipelineCardSequences/setCardSequences', {
      cardId,
      sequences: sequences.value,
    });
  } catch (error) {
    if (requestId !== fetchRequestId || cardId !== props.cardId) return;
    loadError.value = true;
    // eslint-disable-next-line no-console
    console.error('Failed to fetch sequences:', error);
  } finally {
    if (requestId === fetchRequestId) isLoading.value = false;
  }
};

// Pause/resume atualizam o array local E o cache do badge do kanban — sem o
// dispatch, o badge do board ficava com status/cor antigos enquanto o painel
// de detalhe já mostrava o novo estado (achado Codex, ciclo 3a).
const syncBadgeCache = () => {
  store.dispatch('pipelineCardSequences/setCardSequences', {
    cardId: props.cardId,
    sequences: sequences.value,
  });
};

// A matrícula (pai) e a execução (filho) podiam divergir: a UI lia o pai, mas
// pausar/retomar age no filho — então um pai "ativo" cujo filho já terminou
// mostrava um botão que só sabia falhar. O backend agora reconcilia o par e
// devolve a sequência corrigida junto do 422, então trocamos o botão morto pelo
// estado real em vez de só piscar um alerta.
const applyReconciledSequence = (seq, error) => {
  const payload = error?.response?.data;
  if (payload?.data) {
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = payload.data;
    syncBadgeCache();
  }
  return payload;
};

const handleSequenceActionError = (seq, error, logLabel) => {
  const payload = applyReconciledSequence(seq, error);
  if (!payload?.data) {
    // eslint-disable-next-line no-console
    console.error(logLabel, error);
  }
  showAlert(payload?.error || t('PIPELINE.SEQUENCES.UPDATE_ERROR'));
};

const handlePause = async seq => {
  if (!acquireSequenceLock(seq.id)) return;
  try {
    const { data } = await PipelineCardsAPI.pauseSequence(props.cardId, seq.id);
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = data.data;
    syncBadgeCache();
  } catch (error) {
    handleSequenceActionError(seq, error, 'Failed to pause sequence:');
  } finally {
    releaseSequenceLock(seq.id);
  }
};

const handleResume = async seq => {
  if (!acquireSequenceLock(seq.id)) return;
  try {
    const { data } = await PipelineCardsAPI.resumeSequence(
      props.cardId,
      seq.id
    );
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = data.data;
    syncBadgeCache();
  } catch (error) {
    handleSequenceActionError(seq, error, 'Failed to resume sequence:');
  } finally {
    releaseSequenceLock(seq.id);
  }
};

const handleCancel = async seq => {
  if (isConfirmingCancel.value || !acquireSequenceLock(seq.id)) return;
  isConfirmingCancel.value = true;
  let confirmed = false;
  try {
    confirmed = await cancelConfirmationRef.value?.showConfirmation();
  } catch {
    releaseSequenceLock(seq.id);
    return;
  } finally {
    isConfirmingCancel.value = false;
  }
  if (!confirmed) {
    releaseSequenceLock(seq.id);
    return;
  }
  try {
    await PipelineCardsAPI.cancelSequence(props.cardId, seq.id);
    await fetchSequences();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to cancel sequence:', error);
    showAlert(t('PIPELINE.SEQUENCES.UPDATE_ERROR'));
  } finally {
    releaseSequenceLock(seq.id);
  }
};

// Phase 1.2 — Mark current step completed and advance to next.
// Backend endpoint created by Phase 1.1 agent. May 404 during parallel dev.
const handleCompleteStep = async seq => {
  if (!acquireSequenceLock(seq.id)) return;
  try {
    const { data } = await PipelineCardsAPI.completeSequenceStep(
      props.cardId,
      seq.id
    );
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = data.data;
    // Refresh list to get updated step counts and potentially new status
    await fetchSequences();
    showAlert(t('PIPELINE.SEQUENCES.COMPLETE_STEP_SUCCESS'));
  } catch (error) {
    const payload = applyReconciledSequence(seq, error);
    if (!payload?.data) {
      // eslint-disable-next-line no-console
      console.error('Failed to complete sequence step:', error);
    }
    showAlert(payload?.error || t('PIPELINE.SEQUENCES.COMPLETE_STEP_ERROR'));
  } finally {
    releaseSequenceLock(seq.id);
  }
};

const formatNextStep = nextStepAt => {
  if (!nextStepAt) return null;
  try {
    const target = new Date(nextStepAt).getTime();
    if (!Number.isFinite(target)) return '';
    const diffMinutes = Math.round((target - Date.now()) / 60000);
    const formatter = new Intl.RelativeTimeFormat(
      (locale.value || 'en-US').replace('_', '-'),
      { numeric: 'auto' }
    );
    if (Math.abs(diffMinutes) < 60) {
      return formatter.format(diffMinutes, 'minute');
    }
    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) return formatter.format(diffHours, 'hour');
    return formatter.format(Math.round(diffHours / 24), 'day');
  } catch {
    return '';
  }
};

const handleSequenceStarted = () => {
  showStartModal.value = false;
  fetchSequences();
  emit('sequenceStarted');
};

watch([() => props.cardId, isSequencesEnabled], fetchSequences, {
  immediate: true,
});

// Realtime: a sequência avança sozinha no servidor (step agendado, automação).
// Esta seção mantém a própria lista detalhada, então o commit no store — que
// só alimenta o badge — não a atualiza. Refaz o fetch quando o evento é do
// card aberto. (fase-57 RT-P2-8)
const handleRemoteSequenceChange = data => {
  const eventCardId = data?.sequence?.pipeline_card_id;
  if (eventCardId == null) return;
  if (String(eventCardId) !== String(props.cardId)) return;

  fetchSequences();
};

onMounted(() =>
  emitter.on(PIPELINE_EVENTS.SEQUENCE_CHANGED, handleRemoteSequenceChange)
);
onBeforeUnmount(() =>
  emitter.off(PIPELINE_EVENTS.SEQUENCE_CHANGED, handleRemoteSequenceChange)
);
</script>

<template>
  <div v-if="isSequencesEnabled">
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('PIPELINE.SEQUENCES.SECTION_TITLE') }}
      </h4>
      <button
        type="button"
        class="inline-flex min-h-11 items-center gap-1 rounded bg-violet-50 px-3 py-2 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand dark:bg-violet-900/30 dark:text-violet-300 dark:hover:bg-violet-900/50"
        @click="showStartModal = true"
      >
        <fluent-icon icon="add" size="12" />
        {{ t('PIPELINE.SEQUENCES.START_BUTTON') }}
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="text-xs text-n-slate-11 py-2"
      role="status"
      aria-live="polite"
    >
      {{ t('PIPELINE.SEQUENCES.LOADING') }}
    </div>

    <div
      v-else-if="loadError"
      class="flex items-center justify-between gap-3 rounded-lg bg-n-ruby-3 p-3 text-xs text-n-ruby-11"
      role="alert"
    >
      <span>{{ t('PIPELINE.SEQUENCES.LOAD_ERROR') }}</span>
      <button
        type="button"
        class="min-h-11 rounded-lg px-3 font-medium hover:bg-n-ruby-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="fetchSequences"
      >
        {{ t('PIPELINE.SEQUENCES.RETRY') }}
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="sequences.length === 0"
      class="text-xs text-n-slate-11 py-2 text-center"
    >
      {{ t('PIPELINE.SEQUENCES.EMPTY_STATE') }}
    </div>

    <!-- Sequences -->
    <div v-else class="space-y-3">
      <!-- Active/Paused Sequences -->
      <div
        v-for="seq in activeSequences"
        :key="seq.id"
        class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      >
        <!-- Sequence header -->
        <div class="flex items-start justify-between gap-2 mb-2">
          <div class="flex-1 min-w-0">
            <span class="text-xs font-medium text-n-slate-12 truncate block">
              {{ seq.definition?.name || t('PIPELINE.SEQUENCES.UNNAMED') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('PIPELINE.SEQUENCES.STEP_LABEL') }} {{ seq.current_step }} /
              {{ seq.total_steps }}
            </span>
          </div>
          <!-- Status badge -->
          <span
            class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
            :class="
              seq.status === 'active'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{
              seq.status === 'active'
                ? t('PIPELINE.SEQUENCES.STATUS_ACTIVE')
                : t('PIPELINE.SEQUENCES.STATUS_PAUSED')
            }}
          </span>
        </div>

        <!-- Progress bar -->
        <div
          class="h-1.5 bg-n-alpha-2 rounded-full overflow-hidden mb-2"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="seq.progress_percentage || 0"
        >
          <div
            class="h-full bg-violet-500 rounded-full transition-[width] duration-300 motion-reduce:transition-none"
            :style="{ width: `${seq.progress_percentage}%` }"
          />
        </div>

        <!-- Next step info -->
        <p
          v-if="seq.next_step_at && seq.status === 'active'"
          class="text-xs text-n-slate-11 mb-2"
        >
          {{ t('PIPELINE.SEQUENCES.NEXT_STEP') }}:
          {{ formatNextStep(seq.next_step_at) }}
        </p>

        <!-- Actions -->
        <div class="flex items-center gap-2 flex-wrap">
          <!-- Mark step done — only when active and there are more steps -->
          <button
            v-if="seq.status === 'active' && seq.current_step < seq.total_steps"
            v-tooltip="{
              content: t('PIPELINE.SEQUENCES.COMPLETE_STEP_TOOLTIP'),
              delay: { show: 300, hide: 0 },
              placement: 'top',
            }"
            type="button"
            class="inline-flex min-h-11 items-center gap-1 rounded-md border border-grass-6/50 bg-grass-3 px-3 py-2 text-xs font-medium text-grass-11 transition-colors hover:bg-grass-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
            :disabled="isSequenceBusy(seq.id)"
            @click="handleCompleteStep(seq)"
          >
            <fluent-icon icon="checkmark-circle" size="12" />
            {{ t('PIPELINE.SEQUENCES.COMPLETE_STEP') }}
          </button>
          <button
            v-if="seq.status === 'active'"
            type="button"
            class="min-h-11 rounded-md px-3 py-2 text-xs text-n-amber-11 hover:bg-n-amber-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
            :disabled="isSequenceBusy(seq.id)"
            @click="handlePause(seq)"
          >
            {{ t('PIPELINE.SEQUENCES.ACTION_PAUSE') }}
          </button>
          <button
            v-if="seq.status === 'paused'"
            type="button"
            class="min-h-11 rounded-md px-3 py-2 text-xs text-green-600 hover:bg-n-teal-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50 dark:text-green-400"
            :disabled="isSequenceBusy(seq.id)"
            @click="handleResume(seq)"
          >
            {{ t('PIPELINE.SEQUENCES.ACTION_RESUME') }}
          </button>
          <button
            type="button"
            class="ml-auto min-h-11 rounded-md px-3 py-2 text-xs text-red-500 hover:bg-n-ruby-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50 dark:text-red-400"
            :disabled="isSequenceBusy(seq.id)"
            @click="handleCancel(seq)"
          >
            {{ t('PIPELINE.SEQUENCES.ACTION_CANCEL') }}
          </button>
        </div>

        <!-- Steps log (last 3) -->
        <div
          v-if="seq.steps_log?.length > 0"
          class="mt-2 pt-2 border-t border-n-weak"
        >
          <p class="text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PIPELINE.SEQUENCES.STEPS_LOG') }}:
          </p>
          <ul class="space-y-1">
            <li
              v-for="log in seq.steps_log.slice(-3)"
              :key="log.step_number"
              class="text-xs text-n-slate-11 flex items-center gap-1"
            >
              <fluent-icon
                icon="checkmark-circle"
                size="12"
                class="text-green-500 shrink-0"
              />
              {{ log.title }} ({{ log.activity_type }})
            </li>
          </ul>
        </div>
      </div>

      <!-- Completed/Cancelled summary -->
      <div
        v-if="completedSequences.length > 0"
        class="text-xs text-n-slate-11 mt-2"
      >
        {{ completedSequences.length }}
        {{ t('PIPELINE.SEQUENCES.COMPLETED_COUNT') }}
      </div>
    </div>

    <!-- Start Sequence Modal -->
    <StartSequenceModal
      v-if="showStartModal"
      :card-id="cardId"
      @close="showStartModal = false"
      @started="handleSequenceStarted"
    />

    <ConfirmationModal
      ref="cancelConfirmationRef"
      :title="t('PIPELINE.SEQUENCES.ACTION_CANCEL')"
      :description="t('PIPELINE.SEQUENCES.CONFIRM_CANCEL')"
      :confirm-label="t('PIPELINE.SEQUENCES.ACTION_CANCEL')"
      :cancel-label="t('PIPELINE.CANCEL')"
    />
  </div>
</template>
